import { EmptyState, LoadingState, MonoLabel, PageHeader, Star } from '@/component';
import { useFavourites, useHistory, type HistoryRow } from '@/hook';
import { cn } from '@/lib/cn';
import { formatMonthLabel, formatNightLabel, groupByNight } from '@/lib/night';

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** How many people sang that night: everyone named as a co-singer, plus you. */
function singerCount(songs: HistoryRow[]): number {
  return new Set(songs.flatMap((song) => song.coSingers)).size + 1;
}

/** The night's meta line. Venue and system are only there once a device names
 * them, so the singer count carries the line on its own until then. */
function nightMeta(songs: HistoryRow[]): string {
  const [first] = songs;
  return [first?.venue, first?.systemName, plural(singerCount(songs), 'singer')]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

/** /history: every song you have sung, grouped into the nights you sang it. */
export function HistoryPage() {
  const history = useHistory();
  const { favourites, toggle } = useFavourites();

  if (!history) {
    return (
      <>
        <PageHeader title="Singing history" eyebrow="Nights out" />
        <LoadingState />
      </>
    );
  }

  const nights = groupByNight(history);
  const oldest = history.at(-1);
  // Venue and system share a column; without either, the column is dead weight.
  const hasPlaces = history.some((song) => song.venue ?? song.systemName);
  const columns = hasPlaces
    ? 'grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1.4fr)_62px_26px]'
    : 'grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_62px_26px]';

  return (
    <>
      <PageHeader
        title="Singing history"
        eyebrow={
          oldest
            ? `${plural(history.length, 'song')} · ${plural(nights.length, 'night')} · since ${formatMonthLabel(oldest.sangAt)}`
            : 'Nights out'
        }
      />
      {nights.length === 0 && (
        <EmptyState title="Nothing sung yet." body="Queue a song on an Aeon and the night shows up here." />
      )}
      {nights.map((night) => (
        <section key={night.nightStart} className="mt-11">
          <div className="flex flex-wrap items-baseline justify-between gap-5 border-b border-line pb-3">
            <h2 className="text-[19px] font-semibold tracking-[-0.02em]">{formatNightLabel(night.nightStart)}</h2>
            <MonoLabel>{nightMeta(night.songs)}</MonoLabel>
          </div>
          {night.songs.map((song) => (
            <div key={song.historyId} className={cn('grid items-center gap-[18px] border-b border-stripeA py-4', columns)}>
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold tracking-[-0.01em]">{song.title}</div>
                {song.artist && <div className="mt-0.5 text-[13px] text-mute">{song.artist}</div>}
              </div>
              {hasPlaces && (
                <div className="min-w-0">
                  <div className="truncate text-sm">{song.venue}</div>
                  {song.systemName && <MonoLabel className="mt-[3px] block tracking-[0.1em]">{song.systemName}</MonoLabel>}
                </div>
              )}
              <div className="truncate text-[13px] text-mute">{song.coSingers.join(', ')}</div>
              <div className="text-right font-mono text-xs tabular-nums text-mute">× {song.times}</div>
              <Star on={favourites.has(song.trackId)} onToggle={() => toggle(song.trackId)} />
            </div>
          ))}
        </section>
      ))}
    </>
  );
}
